// save name
// save game logs
// save last active game/location

import { userConverter, userToDoc } from 'api/converters/UserConverter';
import { SqRoutes } from 'constants/Routes';
import { ArmyGreenCls, CrimsonCls, Emojis, ValueCls } from 'constants/constants';
import { User, UserCredential } from 'firebase';
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { redirect } from 'react-router-dom';
import {
  getUserAuthState,
  isMounted,
  setFbSqUser,
  setUserAuthState,
  subscribeUserStatusChange,
  UserAuthState,
} from 'slices/SqUserSlice';
import { store } from 'slices/store';
import { Maybe } from 'types/App.types';
import { FbSqUser } from 'types/SqGame.types';
import { auth, db } from '../firebase';

// import { getAuth, signInAnonymously } from 'firebase/auth';

// const auth = getAuth();
// signInAnonymously(auth)
//   .then(() => {
//     // Signed in..
//   })
//   .catch((error) => {
//     const errorCode = error.code;
//     const errorMessage = error.message;
//     // ...
//   });

export const getUserDataFromUserCredential = (user: User): FbSqUser => {
  const maybeDate = user.metadata.creationTime;
  const creationDate = maybeDate ? (new Date(maybeDate).getTime() ?? Date.now()) : Date.now();

  return {
    uid: user.uid,
    email: user.email ?? '',
    name: user.displayName ?? '',
    emailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous,
    isEditor: false,
    createdOn: creationDate,
    modifiedOn: Date.now(),
  };
};

export const createFbSqUserFromUserCredential = async (userModel: UserCredential): Promise<FbSqUser> => {
  const usr = getUserDataFromUserCredential(userModel.user);
  const collectionData = collection(db, 'users').withConverter(userConverter);
  const docRef = doc(collectionData, usr.uid);

  await setDoc(docRef, userToDoc(usr));

  return usr;
};

export const createFbSqUser = async (user: User): Promise<FbSqUser> => {
  const usr = getUserDataFromUserCredential(user);
  const collectionData = collection(db, 'users').withConverter(userConverter);
  const docRef = doc(collectionData, usr.uid);

  await setDoc(docRef, userToDoc(usr));

  return usr;
};

export const updateFbSqUser = async (user: FbSqUser): Promise<FbSqUser> => {
  const userRef = doc(db, `users/${user.uid}`);
  const docUser = userToDoc(user);
  await updateDoc(userRef, docUser);
  store.dispatch(setFbSqUser(user));
  return user;
};

export const loadFbSqUser = async (uid: string): Promise<Maybe<FbSqUser>> => {
  const userRef = doc(db, SqRoutes.getUserRoute(uid)).withConverter(userConverter);
  const docSnap = await getDoc(userRef);
  if (docSnap.exists()) {
    const fbSqUser: FbSqUser = docSnap.data();
    return fbSqUser;
  }
  const msg = `${Emojis.eyes} No such document! %c ${uid} %c`;
  console.log(msg, ValueCls, '');
  throw new Error('USER_NOT_ADDED', { cause: 'No such document!' });
};

export const waitForUserToLogIn = async (): Promise<boolean> => {
  const userAuthState = getUserAuthState(store.getState());
  return new Promise((resolve) => {
    if (userAuthState === UserAuthState.LoggedIn) resolve(true);
    let numberOfTries = 0;
    const timerId = setInterval(
      () => {
        const userState = getUserAuthState(store.getState());
        if (userState === UserAuthState.LoggedIn) {
          resolve(true);
          clearInterval(timerId);
        } else if (userState === UserAuthState.Error) {
          resolve(false);
          clearInterval(timerId);
        } else if (userState === UserAuthState.NotLoggedIn || userState === UserAuthState.LoggedOut) {
          resolve(false);
          clearInterval(timerId);
        } else if (numberOfTries > 20) {
          console.log(`${Emojis.warning} Waited for over ${numberOfTries / 10} seconds and no luck`);
          resolve(false);
          clearInterval(timerId);
        }
        numberOfTries++;
      },
      100,
      numberOfTries
    );
  });
};

export const onUserStateReady = async (): Promise<boolean> => {
  await auth.authStateReady();
  return !!auth.currentUser;
};

export const initializeApp = async (): Promise<boolean> => {
  store.dispatch(subscribeUserStatusChange());
  return await onUserStateReady();
};

export const appLoader = async (): Promise<boolean> => {
  console.log(`${Emojis.voiceAmp} App Loader here`);
  const isMount = isMounted(store.getState());

  if (!isMount) {
    const result = await initializeApp();

    if (result) {
      console.log(`${Emojis.eyes} App user %c exists %c`, ArmyGreenCls, '');
      store.dispatch(setUserAuthState(UserAuthState.LoggedIn));
      return true;
    } else {
      console.log(`${Emojis.eyes} App user %c does not exist %c`, CrimsonCls, '');
      store.dispatch(setUserAuthState(UserAuthState.NotLoggedIn));
      return false;
    }
  } else {
    if (getUserAuthState(store.getState()) === UserAuthState.LoggedIn) return true;
  }
  return false;
};

export const logoutAction = async (): Promise<Response> => {
  console.log(`${Emojis.eyes} Called Action %c logoutAction %c`, ArmyGreenCls, '');

  return redirect(SqRoutes.Login);
};
